import React, { useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { useStaff } from "../hooks/useStaff";
import { useDebounce } from "@/hooks/useDebounce";
import { useCurrentHotelId } from "@/hooks/useCurrentHotelId";

interface UserSelectionListProps {
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
  className?: string;
}

export const UserSelectionList: React.FC<UserSelectionListProps> = ({
  selectedUserIds,
  onSelectionChange,
  className,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const hotelId = useCurrentHotelId();

  const { data: staffResponse, isLoading } = useStaff({
    hotelId: hotelId ?? undefined,
    name: debouncedSearchTerm,
  });

  const allUsers = useMemo(() => staffResponse?.users || [], [staffResponse]);

  const handleSelect = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onSelectionChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectionChange([...selectedUserIds, userId]);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = allUsers.map((user) => user.id);
      // Merge with existing selection to avoid losing selected users not in current search results
      const newSelection = Array.from(new Set([...selectedUserIds, ...allIds]));
      onSelectionChange(newSelection);
    } else {
      // Deselect only visible users? Or all?
      // Usually "Select all" checkbox deselects all.
      // But if we have a search filter, it might be ambiguous.
      // For now, let's deselect all visible users.
      const visibleIds = allUsers.map((user) => user.id);
      onSelectionChange(
        selectedUserIds.filter((id) => !visibleIds.includes(id)),
      );
    }
  };

  const areAllVisibleSelected =
    allUsers.length > 0 &&
    allUsers.every((user) => selectedUserIds.includes(user.id));

  return (
    <div className={cn("border rounded-md bg-white", className)}>
      <div className="p-4 border-b space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 border-gray-200"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="select-all"
            checked={areAllVisibleSelected}
            onCheckedChange={(checked) => handleSelectAll(checked as boolean)}
          />
          <label
            htmlFor="select-all"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Select all
          </label>
        </div>
      </div>

      <div className="max-h-[350px] overflow-y-auto">
        {isLoading && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        )}
        {!isLoading && allUsers.length === 0 && (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No users found
          </div>
        )}
        {!isLoading && allUsers.length > 0 && (
          <div className="divide-y">
            {allUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center space-x-2 p-4 hover:bg-gray-50"
              >
                <Checkbox
                  id={`user-${user.id}`}
                  checked={selectedUserIds.includes(user.id)}
                  onCheckedChange={() => handleSelect(user.id)}
                />
                <label
                  htmlFor={`user-${user.id}`}
                  className="flex-1 text-sm cursor-pointer"
                >
                  <span className="font-medium">
                    {user.firstName} {user.lastName}
                  </span>
                  {user.staff?.position && (
                    <span className="text-muted-foreground ml-1">
                      , {user.staff.position}
                    </span>
                  )}
                </label>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
